// D6-3 / **ITEM 10 — THE FINANCIALS TAB, REBUILT ON REAL STRIPE CHARGES.**
//
// Her superseding money order: *every money number derives from real Stripe charge data, scoped to
// the SELECTED PERIOD, Pacific-bounded, matching Stripe to the cent. A number that does not respond
// to the period buttons is a DEFECT: fix it or remove it.*
//
// **WHAT WAS WRONG, MEASURED BEFORE A LINE WAS WRITTEN (R25/R180), AND IT IS EXACTLY WHAT SHE
// REPORTED.** The block titled *"Revenue by tier"* was not revenue and was not ranged: it was a
// CENSUS — `SELECT tier, count(*) FROM subscriptions WHERE status='active'` — and its own comment
// said so out loud (*"Members per tier, not money per tier"*). It touched the range only through
// `current_period_end >= $1`, so moving the picker from Today to 90 days changed the heading above
// it and nothing in it. It is REPLACED at its declaration rather than left standing beside the real
// one (DL-2/DT-2b/R181), and the census it really was now has its own row, labelled as current
// standing, which is her clause 3.
//
// THE HONESTY LAWS ON THIS TAB, because this is the one she would act on:
//   • **TEST MODE SAYS SO.** A revenue number she reads as real when it is test-mode money is the
//     one wrong number on this dashboard that could cost her something.
//   • **AN ESTIMATE SAYS SO.** Vendor cost is modelled from usage, not invoiced, and net and margin
//     inherit that — so they are labelled too rather than the estimate being laundered into a
//     confident figure one row down.
//   • **REFUNDS ARE NETTED AND STILL VISIBLE.** Her order says netted; the tab's founding law says
//     never silently. Both hold: each lane carries gross, refunded AND net, so the subtraction is on
//     screen and the foot of the table is the net.
//   • **A READ THAT FAILED IS NOT A ZERO** (R18/R65), and `lanesOk: false` is a THIRD state: the
//     total can be exact while the breakdown is unknown, because they are two separate walks of
//     Stripe. That case says so instead of drawing every dollar into "Not yet classified".
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { loadOwnerData, ownerLoadNote } from "../lib/owner-load";
import { beginRead, readMayPaint } from "../lib/latest-read";
import { Download, RefreshCw, ArrowUpRight } from "lucide-react";
import {
  freshnessLabel, money, ESTIMATE_NOTE, TEST_MODE_NOTE, toCsv, csvFilename,
  RECOVERY_ROWS, RECOVERY_NOTE,
} from "../commandCenter.js";
import { downloadCsv } from "../lib/downloads";
import { OwnerBars } from "./OwnerBars";
import { MemberDoor } from "./MemberDoor";

type Lane = {
  key: string; label: string; kind: string; tier: string | null;
  count: number; payers: number;
  grossCents: number; refundedCents: number; netCents: number;
};
type Payer = {
  key: string; memberId: string | null; name: string; email: string;
  tier: string | null; harness: boolean;
  grossCents: number; refundedCents: number; netCents: number;
  charges: number; lanes: string[]; lastMs: number;
};
type Financials = {
  range: { key: string; label: string; days: number; startMs: number; endMs: number };
  purchases: Array<{ product: string; count: number; cents: number }>;
  refunds: { count: number; cents: number };
  costByDay: Array<{ day: string; generations: number; cents: number }>;
  revenue: number | null; vendorCost: number; net: number | null; margin: number | null;
  lanes: Lane[] | null;
  payers: Payer[] | null;
  lanesOk: boolean;
  laneWhy: string;
  stripe: {
    grossCents: number; refundedCents: number; netCents: number;
    charges: number; paidCustomers: number; reconciled: boolean; laneNetCents: number;
  } | null;
  conversion: { signups: number; paid: number | null; pct: number | null; text: string };
  census: {
    paid: Array<{ tier: string; members: number }>;
    granted: number; members: number; free: number; paying: number;
  };
  // L17 — her four, two ranged and two current standing. `RECOVERY_ROWS` decides which is which and
  // the render reads it rather than knowing.
  recovery: { failed: number; recovered: number; inGrace: number; frozen: number } | null;
  priorPeriod: {
    label: string; revenue: number | null; paidCustomers: number | null; signups: number;
    revenueDelta: { pct: number | null; direction: string; text: string };
    paidDelta: { pct: number | null; direction: string; text: string };
    signupsDelta: { pct: number | null; direction: string; text: string };
  };
  testMode: boolean; takenMs: number;
};

const dayLabel = (v: string) => String(v || "").slice(0, 10);
// L17 — **A ROSTERED NUMBER THAT IS NOT IN THE PAYLOAD READS `—`, NEVER `0`.** Zero is an ANSWER on
// this row — *nobody's card failed this month* — so rendering it for a figure that never arrived is
// the one thing this line must not do (R18/R65, and the same ruling as the null guard on her tiles).
const recoveryFigure = (rec: Record<string, number> | null | undefined, key: string) => {
  const v = rec ? rec[key] : undefined;
  return typeof v === "number" && Number.isFinite(v) ? v.toLocaleString("en-US") : "—";
};
const n = (v: number) => Number(v || 0).toLocaleString("en-US");
const tierWord = (t: string) => (t ? t.charAt(0).toUpperCase() + t.slice(1) : "");

/**
 * **THE RECONCILIATION SENTENCE, SAID ON THE TAB'S OWN FACE.**
 *
 * Her clause is *"reconciled to Stripe TO THE CENT per period"*, and a board that merely believes it
 * is reconciled is the board that was already here. Three states, not two (R18): reconciled, NOT
 * reconciled (which names both numbers, because "something is off" is not actionable), and a
 * breakdown that could not be read at all — where the total is still exact and saying nothing would
 * read as agreement.
 *
 * Hookless, so `scripts/item10-money-lanes.mjs` CALLS it over all three.
 */
export function ReconcileNote({ data }: { data?: Financials | null }) {
  if (!data) return null;
  if (!data.stripe) {
    return <p className="owner-page__note owner-fresh--failed" role="status">Stripe could not be read for this period, so there is no total to reconcile{data.laneWhy ? ` (${data.laneWhy})` : ""}.</p>;
  }
  if (!data.lanesOk) {
    return (
      <p className="owner-page__note owner-fresh--failed" role="status">
        The total is {money(data.stripe.netCents)} across {n(data.stripe.charges)} charges and it is exact.
        The breakdown by tier could not be read{data.laneWhy ? ` (${data.laneWhy})` : ""}, so no rows are shown rather than wrong ones.
      </p>
    );
  }
  if (!data.stripe.reconciled) {
    return (
      <p className="owner-page__note owner-fresh--failed" role="status">
        These rows come to {money(data.stripe.laneNetCents)} and Stripe took {money(data.stripe.netCents)} for the same window. Do not act on this table until they agree.
      </p>
    );
  }
  return (
    <p className="owner-page__note">
      Every row below is a real Stripe charge in this window, and they add up to {money(data.stripe.netCents)} —
      {" "}{money(data.stripe.grossCents)} taken less {money(data.stripe.refundedCents)} refunded, across {n(data.stripe.charges)} charges
      {" "}from {n(data.stripe.paidCustomers)} {data.stripe.paidCustomers === 1 ? "person" : "people"}. That is Stripe's own figure for this period, to the cent.
    </p>
  );
}

export function OwnerFinancials({
  rangeQuery, onDenied, onOpenMember,
}: {
  rangeQuery: string; onDenied: () => void; onOpenMember?: (id: string) => void;
}) {
  const [data, setData] = useState<Financials | null>(null);
  const [loading, setLoading] = useState(false);
  const [now, setNow] = useState(Date.now());
  const [note, setNote] = useState("");
  // **THE CLICK-THROUGH, HER CLAUSE 5.** A lane row filters the people list below it to exactly the
  // members behind that row — which is what "every row clicks through to the real members" means on
  // a row that is a GROUP of people rather than one person. Tapping it again clears the filter, so
  // there is no state she can get stuck in with no way back.
  const [lane, setLane] = useState<string>("");

  // R65: the answer is READ. A refused or dropped load used to leave `data` at null with nothing
  // said, so this tab drew itself out of `data?.x || []` and she read a business number that never
  // arrived. The paint is guarded by POSITION (R52) and the note lands on the freshness line, which
  // is the one line whose whole job is saying how current these numbers are.
  //
  // **AND ONLY THE NEWEST READ MAY PAINT. THE WALKTHROUGH FOUND THIS ONE LIVE.** This tab now walks
  // Stripe twice per period change (the lanes, and the prior period), so a read can take many
  // seconds — and with no sequencing, a Today read that resolves AFTER a 7-day read lands last and
  // wins. **The board then shows Today's money under a 7-days button that looks perfectly active**,
  // with the freshness line quietly saying "Today" and nothing anywhere red. Measured on a phone
  // viewport in `item10-financials-walk.mjs`: $80.00 and one payer rendered under 7 days, against
  // Stripe's $15,413.51 and 166.
  //
  // It is a `useRef` and not state, for D-5's reason one room over: a state guard is read from the
  // render that BOUND the handler, so two changes inside one frame both believe they are newest.
  //
  // R216: THE COUNTER ITSELF IS `src/lib/latest-read.ts` NOW, AND IT WAS LIFTED FROM HERE. It was
  // hand-written inline on this one surface while three other surfaces spelled the same law three
  // other ways and a fourth had none at all (R28/R29) — and inside a nine-hook component no gate in
  // this repo could run a line of it. The behaviour is unchanged, deliberately: `beginRead` is the
  // increment-then-take above, and `readMayPaint` is the `!==` below, so this round moves the
  // declaration and touches nothing this tab does.
  const readSeq = useRef(0);
  const load = useCallback(() => {
    const seq = beginRead(readSeq);
    setLoading(true);
    loadOwnerData<Financials>(`/api/owner/command/financials?${rangeQuery}`)
      .then((answer) => {
        // A read that has been superseded is DROPPED whole — not merged, not used for the note —
        // because a stale note under fresh numbers is the same lie one line down.
        if (!readMayPaint(readSeq, seq)) return;
        if (!answer.ok && answer.kind === "denied") { onDenied(); return; }
        if (answer.ok) { setData(answer.data); setNow(Date.now()); }
        setNote(ownerLoadNote(answer));
      })
      .finally(() => { if (readMayPaint(readSeq, seq)) setLoading(false); });
  }, [rangeQuery, onDenied]);
  useEffect(() => { load(); }, [load]);
  // A lane filter is about ONE period's rows. Leaving it set across a period change would show her
  // an empty list under a heading that says there is money in it.
  useEffect(() => { setLane(""); }, [rangeQuery]);

  const lanes = data?.lanes || [];
  const purchases = data?.purchases || [];
  const costByDay = data?.costByDay || [];
  const testMode = Boolean(data?.testMode);
  const laneLabel = lanes.find((l) => l.key === lane)?.label || "";
  const payers = useMemo(() => {
    const all = data?.payers || [];
    return lane ? all.filter((p) => p.lanes.includes(laneLabel)) : all;
  }, [data, lane, laneLabel]) as Payer[];

  const exportCsv = () => {
    if (!data) return;
    const text = [
      toCsv(
        [{ key: "lane", label: "Lane" }, { key: "count", label: "Payments" }, { key: "payers", label: "People" },
          { key: "gross", label: "Gross" }, { key: "refunded", label: "Refunded" }, { key: "net", label: "Net" }],
        lanes.map((l) => ({
          lane: l.label, count: l.count, payers: l.payers,
          gross: money(l.grossCents), refunded: money(l.refundedCents), net: money(l.netCents),
        })),
      ),
      "",
      toCsv(
        [{ key: "name", label: "Paid" }, { key: "email", label: "Email" }, { key: "what", label: "For what" },
          { key: "charges", label: "Payments" }, { key: "net", label: "Net" }],
        (data.payers || []).map((p) => ({
          name: p.name || "(no account)", email: p.email, what: p.lanes.join(" + "),
          charges: p.charges, net: money(p.netCents),
        })),
      ),
      "",
      toCsv(
        [{ key: "product", label: "Purchase" }, { key: "count", label: "Count" }, { key: "amount", label: "Amount" }],
        purchases.map((p) => ({ product: p.product, count: p.count, amount: money(p.cents) })),
      ),
      "",
      toCsv(
        [{ key: "day", label: "Day" }, { key: "generations", label: "Generations" }, { key: "cost", label: "Vendor cost (estimate)" }],
        costByDay.map((c) => ({ day: dayLabel(c.day), generations: c.generations, cost: money(c.cents) })),
      ),
      "",
      toCsv(
        [{ key: "line", label: "Line" }, { key: "amount", label: "Amount" }],
        [
          { line: "Revenue (Stripe, net of refunds)", amount: money(data.revenue || 0) },
          { line: "Paid customers", amount: String(data.stripe?.paidCustomers ?? "") },
          { line: "Conversion", amount: data.conversion?.text || "" },
          { line: "Vendor cost (estimate)", amount: money(data.vendorCost) },
          { line: "Net (estimate)", amount: money(data.net || 0) },
          { line: "Margin", amount: data.margin === null ? "" : `${data.margin}%` },
          // L17 — her recovery line travels in the export too, each row carrying whether it is of
          // the period or of right now. A CSV column with no such word is four numbers under one
          // date, which is the same misreading the screen's labels exist to prevent.
          ...RECOVERY_ROWS.map((r: { key: string; label: string; ranged: boolean }) => ({
            line: `${r.label}${r.ranged ? ` (${data.range?.label || "this period"})` : " (right now)"}`,
            amount: recoveryFigure(data.recovery as unknown as Record<string, number>, r.key),
          })),
          { line: "Mode", amount: testMode ? "test" : "live" },
        ],
      ),
    ].join("\r\n");
    downloadCsv(csvFilename("financials", data.range), text);
  };

  return (
    <div className="owner-financials">
      <div className="owner-overview__bar">
        <p className="owner-overview__fresh">
          {/* R65: the note SUPERSEDES the freshness label — a line saying how current a number is
              must never survive a read that did not land (see OwnerAnalytics for the full ruling). */}
          {note ? <span className="owner-fresh--failed" role="status">{note}</span>
            : data ? <>Taken {freshnessLabel(data.takenMs, now)} · {data.range?.label}</> : loading ? "Reading..." : "..."}
        </p>
        <span className="owner-analytics__acts">
          <button type="button" className="owner-link" onClick={load} disabled={loading}>
            <RefreshCw size={14} />{loading ? "Reading..." : "Refresh"}
          </button>
          <button type="button" className="owner-link" onClick={exportCsv} disabled={!data}>
            <Download size={14} />Export CSV
          </button>
        </span>
      </div>

      {/* THE CAVEAT RIDES THE LABEL, NOT THE NUMBER. `moneyLabel` puts "(test mode)" inside the
          figure, which is right in a sentence and wrong in a tile: at 393px "$8,788.00 (test mode)"
          wrapped onto three lines and the number stopped reading as a number. Said once per tile,
          under it, and again in full at the foot of the tab. */}
      <div className="owner-stats">
        <div className="owner-stat">
          <strong>{data?.revenue === null || data?.revenue === undefined ? "—" : money(data.revenue)}</strong>
          <span>Revenue{testMode ? ` · ${TEST_MODE_NOTE}` : ""}</span>
        </div>
        {/* ITEM 10 — the same number the Overview tile shows, off the same read. */}
        <div className="owner-stat">
          <strong>{data?.stripe ? n(data.stripe.paidCustomers) : "—"}</strong>
          <span>Paid customers</span>
        </div>
        <div className="owner-stat">
          <strong>{data ? money(data.vendorCost) : "—"}</strong><span>Vendor cost · {ESTIMATE_NOTE}</span>
        </div>
        <div className="owner-stat">
          <strong>{data?.net === null || data?.net === undefined ? "—" : money(data.net)}</strong><span>Net · {ESTIMATE_NOTE}</span>
        </div>
        <div className="owner-stat">
          <strong>{data?.margin === null || data?.margin === undefined ? "—" : `${data.margin}%`}</strong>
          <span>Margin · {ESTIMATE_NOTE}</span>
        </div>
      </div>

      {/* HER CLAUSE 2 — THE CONVERSION LINE, the same sentence the Overview carries, from the same
          law (`conversionLine`), so the two screens cannot word it differently or divide differently. */}
      {data ? <p className="owner-financials__conversion">{data.conversion?.text}</p> : null}

      {/* HER CLAUSE 1 — REVENUE BY TIER FOR THE PERIOD. Every row is a lane of real charges, and the
          rows PARTITION them, so the foot is Stripe's own figure. */}
      <h3 className="owner-detail__sub">Where the money came from · {data?.range?.label || ""}</h3>
      <ReconcileNote data={data} />
      {data && data.lanesOk ? (
        lanes.length ? (
          <div className="owner-table-wrap"><table className="owner-table owner-lanes">
            <thead>
              <tr><th>Lane</th><th>Payments</th><th>People</th><th>Gross</th><th>Refunded</th><th>Net</th></tr>
            </thead>
            <tbody>
              {lanes.map((l) => (
                <tr key={l.key} className={lane === l.key ? "is-open" : ""}>
                  <td data-label="Lane">
                    <button type="button" className="owner-link" onClick={() => setLane(lane === l.key ? "" : l.key)}
                      aria-label={`${l.label} — show who paid`}>
                      {l.label}<ArrowUpRight size={13} />
                    </button>
                  </td>
                  <td data-label="Payments">{n(l.count)}</td>
                  <td data-label="People">{n(l.payers)}</td>
                  <td data-label="Gross">{money(l.grossCents)}</td>
                  <td data-label="Refunded">{l.refundedCents ? money(l.refundedCents) : "—"}</td>
                  <td data-label="Net">{money(l.netCents)}</td>
                </tr>
              ))}
              <tr className="owner-lanes__total">
                <td data-label="Lane"><strong>Total</strong></td>
                <td data-label="Payments">{n(data.stripe?.charges || 0)}</td>
                <td data-label="People">{n(data.stripe?.paidCustomers || 0)}</td>
                <td data-label="Gross">{money(data.stripe?.grossCents || 0)}</td>
                <td data-label="Refunded">{money(data.stripe?.refundedCents || 0)}</td>
                <td data-label="Net"><strong>{money(data.stripe?.netCents || 0)}</strong></td>
              </tr>
            </tbody>
          </table></div>
        ) : <p className="owner-page__note">Nobody paid in this window.</p>
      ) : null}

      {/* WHO PAID AND FOR WHAT — the list her Paid customers tile opens. */}
      <h3 className="owner-detail__sub">
        Who paid{lane ? ` · ${laneLabel}` : ""}
        {lane ? <button type="button" className="owner-link owner-lanes__clear" onClick={() => setLane("")}>Show everyone</button> : null}
      </h3>
      {data?.payers ? (
        payers.length ? (
          <div className="owner-table-wrap"><table className="owner-table owner-payers">
            <thead><tr><th>Member</th><th>For what</th><th>Payments</th><th>Net</th></tr></thead>
            <tbody>
              {payers.map((p) => (
                <tr key={p.key}>
                  <td data-label="Member">
                    <MemberDoor id={p.memberId} name={p.name} email={p.email} onOpen={onOpenMember || (() => {})} />
                    {/* W11-1's harness filter does NOT apply to a money row: a test seat that
                        completed a real charge is money Stripe took, so it is shown and FLAGGED
                        rather than hidden, which would break the reconciliation above. */}
                    {p.harness ? <em className="owner-tag">test seat</em> : null}
                  </td>
                  <td data-label="For what">{p.lanes.join(" + ")}</td>
                  <td data-label="Payments">{n(p.charges)}</td>
                  <td data-label="Net">{money(p.netCents)}</td>
                </tr>
              ))}
            </tbody>
          </table></div>
        ) : <p className="owner-page__note">{lane ? "Nobody is in that lane." : "Nobody paid in this window."}</p>
      ) : null}

      {/* HER CLAUSE 4 — THIS PERIOD AGAINST THE PRIOR SAME-LENGTH PERIOD. */}
      {data ? (
        <>
          <h3 className="owner-detail__sub">Against the period before ({data.priorPeriod?.label})</h3>
          <div className="owner-stats owner-stats--compare">
            <div className="owner-stat">
              <strong>{data.priorPeriod?.revenue === null ? "—" : money(data.priorPeriod?.revenue || 0)}</strong>
              <span>Revenue then{data.priorPeriod?.revenueDelta?.text ? ` · ${data.priorPeriod.revenueDelta.text}` : ""}</span>
            </div>
            <div className="owner-stat">
              <strong>{data.priorPeriod?.paidCustomers === null ? "—" : n(data.priorPeriod?.paidCustomers || 0)}</strong>
              <span>Paid customers then{data.priorPeriod?.paidDelta?.text ? ` · ${data.priorPeriod.paidDelta.text}` : ""}</span>
            </div>
            <div className="owner-stat">
              <strong>{n(data.priorPeriod?.signups || 0)}</strong>
              <span>Signups then{data.priorPeriod?.signupsDelta?.text ? ` · ${data.priorPeriod.signupsDelta.text}` : ""}</span>
            </div>
          </div>
        </>
      ) : null}

      {/* HER CLAUSE 3 — THE MEMBERSHIP CENSUS, CURRENT STANDING. Deliberately NOT ranged, and it says
          so: "how many people are paying right now" is not a question about a window, and scoping it
          to one would make this row disagree with the Members tab. */}
      {data?.census ? (
        <>
          <h3 className="owner-detail__sub">Membership right now · not the period</h3>
          <OwnerBars
            rows={[
              ...data.census.paid.map((t) => ({ key: `paid-${t.tier}`, label: `${tierWord(t.tier)} · paying`, value: t.members, note: "paid" })),
              { key: "granted", label: "Complimentary", value: data.census.granted, note: "granted" },
              { key: "free", label: "Free", value: data.census.free, note: "no membership" },
            ]}
            empty="No members."
          />
          <p className="owner-page__note">
            {n(data.census.paying)} paying, {n(data.census.granted)} complimentary and {n(data.census.free)} free
            {" "}— {n(data.census.members)} members in total. Members holding both a gift and a paid
            membership are counted once, as paying.
          </p>
        </>
      ) : null}

      {/* L17 — **THE RECOVERY LINE.** Her four: failed, in grace, recovered, frozen. It sits BELOW the
          reconciled lane table on purpose — everything above it is Stripe's own money to the cent,
          and these are the house's own records, so the note under it says so rather than letting
          them inherit a claim that is not theirs (`lanesOk`'s precedent).

          **WHICH NUMBERS ARE RANGED IS READ OFF THE ROSTER, NEVER KNOWN HERE.** Two of these
          happened inside the window and two are true of this minute whatever the picker says, so a
          render that decided it itself would be a second answer to a question the payload already
          answers — and the one that drifted would put a date on a number that has none. */}
      {data && data.recovery ? (
        <>
          <h3 className="owner-detail__sub">Payment recovery</h3>
          <div className="owner-stats">
            {RECOVERY_ROWS.map((r: { key: string; label: string; ranged: boolean }) => (
              <div className="owner-stat" key={r.key}>
                <strong>{recoveryFigure(data.recovery as unknown as Record<string, number>, r.key)}</strong>
                <span>{r.label}{r.ranged ? ` · ${data.range?.label || ""}` : ""}</span>
              </div>
            ))}
          </div>
          <p className="owner-page__note">{RECOVERY_NOTE}</p>
        </>
      ) : null}

      <div className="owner-two">
        <div>
          <h3 className="owner-detail__sub">One-time purchases recorded here</h3>
          {/* This is the house's OWN `purchases` table, not Stripe's — the same money appears in the
              lane table above as a Stripe charge. It is kept because it is the record of what was
              UNLOCKED, and the refund line under it is what makes the exclusion visible. */}
          <OwnerBars
            rows={purchases.map((p) => ({ key: p.product, label: p.product, value: p.cents, note: `${p.count}` }))}
            empty="No purchases in this window."
            format={(cents) => money(cents)}
          />
          <p className="owner-page__note">
            {data?.refunds?.count
              ? `${data.refunds.count} refund${data.refunds.count === 1 ? "" : "s"} in this window · ${money(data.refunds.cents)} taken back. Refunded purchases are already out of the table above.`
              : "No refunds in this window."}
          </p>
        </div>
        <div>
          <h3 className="owner-detail__sub">Vendor cost by day · {ESTIMATE_NOTE}</h3>
          <OwnerBars
            rows={costByDay.map((c) => ({
              key: dayLabel(c.day), label: dayLabel(c.day), value: c.cents,
              note: `${c.generations} generated`,
            }))}
            empty="Nothing was generated in this window, so nothing was spent."
            format={(cents) => money(cents)}
          />
        </div>
      </div>

      {testMode ? (
        <p className="owner-page__note">
          Billing is in test mode, so every figure here is test money. It is wired for live and will
          read the same way the day the live key goes in.
        </p>
      ) : null}
    </div>
  );
}
